import './polyfill';
import adapterManager from './adapter';
import myUtil from './util/index';

import pkg from '../package.json';

import status from '../constant/GNBStatus';
import ERRCODE from '../constant/errorCode';
import BaseAdapter from './adapter/base';

const { version } = pkg;

const { Logger, isFunc, warpFuncPromise, emptyFunc } = myUtil;

class BEXT {
  constructor() {
    this.adapter = null; // adapter 代理
    this.status = status.BEXT_OK; // 默认状态
    this.version = version;
    this.debug = false; // 设置 debug 模式
    this.logger = new Logger('BExt'); // 默认 logger
    this.adapterConfig = null;
    this.readycbQueue = [];
    this.errorcb = emptyFunc;
    this.failbackAdapter = null;
    this.beforeConfigHook = emptyFunc; // 介于 sdk loaded 和 sdk config 之间
    return proxyFunc(this); // Proxy 代理
  }

  register(adapterWrap) {
    adapterManager.add(adapterWrap);
  }

  setMode(mode) {
    this.debug = mode;
    this.logger.setMode(mode);
  }

  config(opts = {}, configOpts = {}) {
    const { callWithFail, enableFailback = true } = configOpts;
    let computedCallWithFail = enableFailback || callWithFail;
    if (typeof opts !== 'object') {
      this._errHandle(new Error('Config method arguments must be object!'));
      return;
    }
    let handle;
    if (this.adapter) {
      // 已经存在 Adapter 所以直接走 config 流程
      handle = warpFuncPromise(this.beforeConfigHook || emptyFunc)
        .then(() => this.adapter.config(opts));
    } else {
      this.adapterConfig = opts;
      handle = adapterManager.getAdapter(opts)
        .then((adp) => {
          this.adapter = adp;
          this.status = status.ADAPTER_OK;
          return warpFuncPromise(this.beforeConfigHook || emptyFunc);
        })
        .then(() => this.adapter.config(opts))
        .then(() => {
          this.status = status.OK;
        });
    }
    if (computedCallWithFail) {
      // 认证失败也继续执行 ready 中的方法
      handle = handle
        .catch((err) => {
          if (enableFailback) {
            this.adapter = new BaseAdapter(opts);
            this.status = status.OK;
          }
          this.errorcb(err);
          this.errorcb = () => {}; // 重置 errorcb

          // 再将错误抛出去，支持 config 的 Promise 化
          throw err;
        })
        .finally(() => {
          while (this.readycbQueue.length) {
            // 执行 ready callback
            this.readycbQueue.shift()();
          }
        });
    } else {
      handle = handle
        .then(() => {
          while (this.readycbQueue.length) {
          // 执行 ready callback
            this.readycbQueue.shift()();
          }
        })
        .catch((err) => {
          if (enableFailback) {
            this.adapter = new BaseAdapter(opts);
            this.status = status.OK;
          }
          this.errorcb(err);
          this.errorcb = () => {}; // 重置 errorcb

          // 再将错误抛出去，支持 config 的 Promise 化
          throw err;
        });
    }
    return handle;
  }

  ready(cb) {
    if (!isFunc(cb)) {
      // err catch
      this._errHandle(new Error('Ready method argument must be function!'));
      return;
    }

    if (this.status === status.OK) {
      // 所有配置都已OK，直接执行
      cb();
      return;
    }
    this.readycbQueue.push(cb);
  }

  error(cb) {
    if (!isFunc(cb)) {
      this._errHandle(new Error('Error method must be function!'));
      return;
    }

    this.errorcb = cb;
  }

  _errHandle(err) {
    this.logger.error(err);
  }

  use(method, ...args) {
    this._execAdapter(true, method, ...args);
  }

  isApiSupported(args) {
    const { adapter } = this;
    const { apiName, success = () => {}, fail = () => {} } = args;

    if (!adapter) {
      return fail(Object.assign({}, ERRCODE.NOT_READY, {
        errorMsg: 'Bridge not ready: ' + this.status
      }));
    }

    if (adapter.isApiSupported) {
      return this._execAdapter(false, 'isApiSupported', args);
    }

    if (isFunc(adapter[apiName]) || isFunc(adapter.sdk && adapter.sdk[apiName])) {
      return success(true);
    }

    return success(false);
  }

  _preExecAdapter(prop) {
    if (this.status !== status.OK) {
      this.logger.info('Not OK!');
    }

    let adapterProp;
    if (this.adapter) {
      adapterProp = this.adapter[prop];
    }
    if (adapterProp === undefined || isFunc(adapterProp)) {
      adapterProp = (...args) => this._execAdapter(false, prop, ...args);
      // 因为 KNB 的 share 导致我们必须要特殊的处理。http://docs.sankuai.com/doc/knb/knb-doc/list/share/#_4
      if (prop === 'share') {
        Object.assign(adapterProp, {
          'WECHAT_FRIENDS': 'WECHAT_FRIENDS',
          'WECHAT_TIMELINE': 'WECHAT_TIMELINE',
          'QQ': 'QQ',
          'SMS': 'SMS',
          'WEIBO': 'WEIBO',
          'QZONE': 'QZONE',
          'EMAIL': 'EMAIL'
          // 'COPY': ''  // todo: not supported by hbnb
        });
      }
    }
    return adapterProp;
  }

  /**
     * 所有 API 方法的调用入口
     * @param {boolean} callSDK 是否是直接调用 SDK 方法
     * @param {string} method 方法名
     * @param {object} args 参数
     */
  _execAdapter(callSDK, method, args = {}) {
    args = wrapArgs(method, args, this);
    if (typeof method !== 'string') {
      return args.fail(Object.assign({}, ERRCODE.PARAMS_ERROR, {
        errorMsg: 'Invalid method string!'
      }));
    }
    if (this.status !== status.OK) {
      return args.fail(Object.assign({}, ERRCODE.NOT_READY, {
        errorMsg: 'Bridge not ready: ' + this.status
      }));
    }

    if (callSDK || this.adapter[method] === undefined) {
      this.adapter._execSDK(method, args);
      return;
    }
    this.adapter[method](args);
  }
}

const defaultArgs = {
  success() {},
  fail() {},
  cancel() {},
  trigger() {}
};
function wrapArgs(method, args, that) {
  const { debug, logger } = that;

  const newArgs = Object.assign({}, defaultArgs, args);
  if (debug) {
    logger.debug('call: ' + method, JSON.stringify(args)); // 打印传入参数
    newArgs.success = (...res) => {
      // 这是一种侵入性比较强的方式，后面可以改一下
      logger.debug('success: ' + method, res.map(v => typeof v === 'object' ? JSON.stringify(v) : v)); // 打印输出参数
      args.success && args.success(...res);
    };
    newArgs.fail = (...res) => {
      logger.debug('fail: ' + method, res.map(v => typeof v === 'object' ? JSON.stringify(v) : v));
      args.fail && args.fail(...res);
    };
  }

  return newArgs;
}

function proxyFunc (obj) {
  const handle = {
    get (target, prop, receiver) {
      const originV = target[prop];
      if (originV === undefined) { // 假如主体上没有这个方法就走 adapter，后续需要调整
        return target._preExecAdapter(prop);
      }

      if (isFunc(originV)) {
        return target[prop].bind(target);
      }

      return originV;
    }
  };
  return new Proxy(obj, handle);
}

export default BEXT;
